// Pictures for cards, from Wikipedia. Their size is known before they are shown, so a card opens at its final
// size and the picture fades into a space already made for it: nothing grows, nothing jumps.

export interface Plate {
  /** Sources to try in order: a sharp one, then Wikipedia's own small thumbnail. */
  sources: string[];
  width: number;
  height: number;
}

/** Wikimedia serves thumbnails only at these widths; any other is refused. */
const THUMB_WIDTHS = [330, 500, 960, 1280];
/** Twice a card's width, for sharp screens. */
const WANT = 800;

/**
 * Wikipedia tags picture links with tracking parameters (utm_source…). Content blockers, common in Safari, may
 * block requests that carry them; the pictures are the same without, so they are dropped.
 */
const clean = (url: string) => url.split("?")[0] ?? url;

const plates = new Map<string, Promise<Plate | null>>();
const images = new Map<string, Promise<HTMLImageElement | null>>();

/** A picture's sources and true proportions, fetched once per article. */
export function plateFor(title: string): Promise<Plate | null> {
  let p = plates.get(title);
  if (!p) {
    type Img = { source: string; width: number; height: number };
    p = fetch(`https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(title.replace(/ /g, "_"))}`)
      .then((r) => (r.ok ? (r.json() as Promise<{ thumbnail?: Img; originalimage?: Img }>) : null))
      .then((j): Plate | null => {
        const thumb = j?.thumbnail,
          original = j?.originalimage;
        if (!thumb) return null;
        const small = clean(thumb.source);
        const plate: Plate = { sources: [small], width: thumb.width, height: thumb.height };
        if (!original || original.width <= thumb.width || !/\/\d+px-/.test(small)) return plate;
        const width = THUMB_WIDTHS.filter((w) => w < original.width).find((w) => w >= WANT);
        if (!width || width <= thumb.width) return plate;
        return {
          sources: [small.replace(/\/\d+px-/, `/${width}px-`), small],
          width,
          height: Math.round((original.height * width) / original.width),
        };
      })
      .catch(() => null);
    plates.set(title, p);
  }
  return p;
}

/** Loads (and decodes) a plate's picture, trying each source in turn. Cached, so pointing ahead pays off. */
export function imageFor(plate: Plate): Promise<HTMLImageElement | null> {
  const key = plate.sources[0] ?? "";
  let p = images.get(key);
  if (!p) {
    p = (async () => {
      for (const src of plate.sources) {
        const img = new Image();
        img.decoding = "async";
        img.alt = "";
        const ok = await new Promise<boolean>((resolve) => {
          // Listeners first, then the source, so a picture already in the cache is never missed.
          img.onload = () => resolve(true);
          img.onerror = () => resolve(false);
          img.src = src;
        });
        if (!ok) continue;
        await img.decode().catch(() => undefined);
        return img;
      }
      return null;
    })();
    images.set(key, p);
  }
  return p;
}

/** Starts fetching an article's picture ahead of need (when the pointer comes to rest on it). */
export function prefetch(title: string | undefined): void {
  if (title) void plateFor(title).then((p) => p && imageFor(p));
}
